import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import ClockStatusWidget from './ClockStatusWidget';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import HelpMenu from './HelpMenu';
import ProfileMenu from './ProfileMenu';
import WhatsNewDialog from './WhatsNewDialog';
import LanguagePromptDialog from './LanguagePromptDialog';
import OfflineSyncBanner from './OfflineSyncBanner';
import GraceBanner from './GraceBanner';
import AskHinesTime from './AskHinesTime';
import { Clock, X, Search, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MoreSheet, mobileBarItems } from './BottomNav';
import { navForUser } from './nav/navConfig';


const PAGE_TITLES = {
  '/home': 'Home',
  '/dashboard': 'Dashboard',
  '/worker': 'My Day',
  '/jobs': 'Job Management',
  '/jobs/board': 'Jobs Board',
  '/boards': 'Boards',
  '/workplans': 'Workplans',
  '/notes': 'Notes',
  '/manage-users': 'Staff Management',
  '/audit-log': 'Audit Log',
  '/manage-teams': 'Team Management',
  '/map': 'Live Map',
  '/reports': 'Reports & Analytics',
  '/settings': 'Settings',
  '/error-log': 'Error Log',
  '/teams': 'My Team',
};

export default function AppLayout({ children }) {
  const { t } = useTranslation();
  const { user, company, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [clockSheetOpen, setClockSheetOpen] = useState(false);
  const [mobileStatus, setMobileStatus] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const role = user?.role || 'user';
  const mobileItems = mobileBarItems(user);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = navForUser(user).some((n) => n.items && !mobileItems.some((b) => n.items.includes(b)) && n.items.some((i) => location.pathname === i.path.split('?')[0]));
  const pageTitle = PAGE_TITLES[location.pathname] || '';
  const sidebarWidth = sidebarCollapsed ? 68 : 240;

  return (
    <div className="min-h-screen" style={{ background: '#0B1321' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      )}

      {/* Main Content Area */}
      <div
        className="flex flex-col min-h-screen transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : sidebarWidth }}
      >
        {/* Desktop Top Header */}
        {!isMobile && (
          <header
            className="sticky top-0 z-40 h-14 flex items-center gap-2 lg:gap-4 px-3 lg:px-6"
            style={{ background: '#0B1321', borderBottom: '1px solid #2D3748' }}
            data-testid="app-header"
          >
            <h1 className="text-lg font-semibold text-white truncate min-w-0 flex-shrink">{pageTitle}</h1>
            <div className="flex-1 min-w-0 max-w-xl mx-auto">
              <GlobalSearch token={token} role={role} />
            </div>
            <div className="flex items-center gap-1.5 xl:gap-4 flex-shrink-0">
              <ClockStatusWidget variant="compact" />
              <HelpMenu />
              <NotificationBell />
              <ProfileMenu variant="desktop" />
            </div>
          </header>
        )}

        {/* Mobile Top Header (search icon + profile menu) — shown for all roles
            so workers can also reach Notification Preferences / Sign out. */}
        {isMobile && (
          <header
            className="sticky top-0 z-40 h-12 flex items-center justify-between px-4"
            style={{ background: '#0B1321', borderBottom: '1px solid #2D3748' }}
            data-testid="app-header-mobile"
          >
            <h1 className="text-base font-semibold text-white truncate">{pageTitle}</h1>
            <div className="flex items-center gap-1">
              {(role === 'admin' || role === 'supervisor') && (
                <>
                  <HelpMenu compact />
                  <button
                    onClick={() => setMobileSearchOpen(true)}
                    className="p-2 rounded-lg hover:bg-white/5"
                    aria-label="Open search"
                    data-testid="mobile-search-btn"
                  >
                    <Search className="w-5 h-5" style={{ color: '#A0AEC0' }} />
                  </button>
                </>
              )}
              <NotificationBell />
              <ProfileMenu variant="mobile" />
            </div>
          </header>
        )}

        {/* Mobile search overlay */}
        {isMobile && mobileSearchOpen && (
          <GlobalSearch
            token={token}
            role={role}
            isMobile={true}
            onCloseMobile={() => setMobileSearchOpen(false)}
          />
        )}

        {/* Page Content */}
        <main
          className="flex-1 overflow-auto ht-page-dark"
          style={{ paddingBottom: isMobile ? 80 : 0, background: '#0B1321' }}
        >
          <GraceBanner />
          <OfflineSyncBanner />
          {children}
        </main>
        <AskHinesTime />

        {/* One-time Welcome / What's New popup (per release version) */}
        <WhatsNewDialog />
        <LanguagePromptDialog />

        {/* Mobile: Auto-out banner + compact widget lives hidden above so banner can show */}
        {isMobile && (
          <div className="hidden">
            {/* Hidden instance keeps auto-out banner active on all pages even when sheet closed */}
            <ClockStatusWidget variant="compact" onStatusChange={setMobileStatus} />
          </div>
        )}

        {/* Mobile Bottom Nav - Glassmorphism */}
        {isMobile && (
          <nav
            className="fixed bottom-0 left-0 right-0 z-50"
            style={{
              background: 'rgba(15,25,35,0.85)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(45,55,72,0.6)'
            }}
            data-testid="mobile-nav"
          >
            <div
              style={{ display: 'grid', gridTemplateColumns: `repeat(${mobileItems.length + 2}, 1fr)` }}
              className="w-full py-1"
            >
              {mobileItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path.split('?')[0];
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className="flex flex-col items-center justify-center py-2 transition-all"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    <div className="p-1.5 rounded-xl mb-0.5 transition-all" style={isActive ? { background: 'rgba(30,136,229,0.15)' } : {}}>
                      <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.5} style={{ color: isActive ? '#1E88E5' : '#64748B' }} />
                    </div>
                    <span className="text-[10px] font-medium" style={{ color: isActive ? '#1E88E5' : '#64748B' }}>{t(item.tKey, item.name)}</span>
                  </Link>
                );
              })}
              {/* More: every other page, grouped like the sidebar (Sep 2026) */}
              <button onClick={() => setMoreOpen(true)} className="flex flex-col items-center justify-center py-2 transition-all" data-testid="mobile-nav-more">
                <div className="p-1.5 rounded-xl mb-0.5" style={moreActive ? { background: 'rgba(30,136,229,0.15)' } : {}}>
                  <Menu className="w-5 h-5" strokeWidth={moreActive ? 2.5 : 1.5} style={{ color: moreActive ? '#1E88E5' : '#64748B' }} />
                </div>
                <span className="text-[10px] font-medium" style={{ color: moreActive ? '#1E88E5' : '#64748B' }}>{t('nav.more', 'More')}</span>
              </button>
              {/* Clock quick action */}
              <button
                onClick={() => setClockSheetOpen(true)}
                className="flex flex-col items-center justify-center py-2 transition-all relative"
                data-testid="mobile-nav-clock"
              >
                <div
                  className="p-1.5 rounded-xl mb-0.5 transition-all relative"
                  style={mobileStatus?.clockedIn ? { background: 'rgba(0,200,150,0.15)' } : {}}
                >
                  <Clock
                    className="w-5 h-5"
                    strokeWidth={mobileStatus?.clockedIn ? 2.5 : 1.5}
                    style={{ color: mobileStatus?.clockedIn ? '#00C896' : '#64748B' }}
                  />
                  {mobileStatus?.clockedIn && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#00C896' }} />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: '#00C896' }} />
                    </span>
                  )}
                </div>
                <span
                  className="text-[10px] font-medium"
                  style={{ color: mobileStatus?.clockedIn ? '#00C896' : '#64748B' }}
                >
                  {mobileStatus?.clockedIn ? t('nav.onClock', 'On Clock') : t('home.clockIn', 'Clock In')}
                </span>
              </button>
            </div>
          </nav>
        )}

        {isMobile && <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />}

        {/* Mobile bottom sheet */}
        {isMobile && clockSheetOpen && (
          <div
            className="fixed inset-0 z-[60] flex items-end"
            style={{ background: 'rgba(11,19,33,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setClockSheetOpen(false)}
            data-testid="clock-sheet-backdrop"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded-t-3xl pb-8 pt-4 px-4"
              style={{ background: '#0F1923', borderTop: '1px solid #2D3748' }}
              data-testid="clock-bottom-sheet"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-base font-semibold text-white">Time Clock</h3>
                <button onClick={() => setClockSheetOpen(false)} className="p-2 rounded-full" style={{ color: '#A0AEC0' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <ClockStatusWidget variant="full" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
